import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Vehicle } from "../types";

interface CreateInspectionModalProps {
  show: boolean;
  isDarkMode: boolean;
  vehicleSearch: string;
  searchResults: Vehicle[];
  selectedVehicle: Vehicle | null;
  driverName: string;
  mechanicName: string;
  isSearching: boolean;
  isCreating: boolean;
  onVehicleSearchChange: (value: string) => void;
  onVehicleSelect: (vehicle: Vehicle) => void;
  onDriverNameChange: (value: string) => void;
  onMechanicNameChange: (value: string) => void;
  onClose: () => void;
  onCreate: () => void;
}

export const CreateInspectionModal: React.FC<CreateInspectionModalProps> = ({
  show,
  isDarkMode,
  vehicleSearch,
  searchResults,
  selectedVehicle,
  driverName,
  mechanicName,
  isSearching,
  isCreating,
  onVehicleSearchChange,
  onVehicleSelect,
  onDriverNameChange,
  onMechanicNameChange,
  onClose,
  onCreate,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Auto-advance to step 2 when vehicle is selected
  useEffect(() => {
    if (selectedVehicle && currentStep === 1) {
      setCurrentStep(2);
    }
  }, [selectedVehicle, currentStep]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowVehicleDropdown(false);
      }
    };

    if (showVehicleDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showVehicleDropdown]);

  // Reset step when modal closes
  useEffect(() => {
    if (!show) {
      setCurrentStep(1);
      setShowVehicleDropdown(false);
    }
  }, [show]);

  const handleVehicleSelect = (vehicle: Vehicle) => {
    onVehicleSelect(vehicle);
    setShowVehicleDropdown(false);
  };

  const handleClearVehicle = () => {
    onVehicleSelect(null as any);
    setCurrentStep(1);
    setShowVehicleDropdown(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => !isCreating && onClose()}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
            style={{
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              className={`px-6 py-5 border-b ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    Create New Inspection
                  </h3>
                  <p
                    className={`mt-1 text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Select a vehicle and provide inspection details
                  </p>
                </div>
                <button
                  onClick={() => !isCreating && onClose()}
                  disabled={isCreating}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode
                      ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  } disabled:opacity-50`}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Progress Steps */}
              <div className="flex items-center justify-center space-x-4">
                {/* Step 1 */}
                <div className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold transition-all ${
                      currentStep >= 1
                        ? isDarkMode
                          ? "bg-phoenix-600 border-phoenix-600 text-white"
                          : "bg-phoenix-600 border-phoenix-600 text-white"
                        : isDarkMode
                        ? "border-gray-600 text-gray-600"
                        : "border-gray-300 text-gray-400"
                    }`}
                  >
                    {currentStep > 1 ? (
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      "1"
                    )}
                  </div>
                  <span
                    className={`ml-2 text-sm font-medium ${
                      currentStep >= 1
                        ? isDarkMode
                          ? "text-gray-200"
                          : "text-gray-900"
                        : isDarkMode
                        ? "text-gray-600"
                        : "text-gray-400"
                    }`}
                  >
                    Select Vehicle
                  </span>
                </div>

                {/* Divider */}
                <div
                  className={`w-16 h-0.5 ${
                    currentStep >= 2
                      ? isDarkMode
                        ? "bg-phoenix-600"
                        : "bg-phoenix-600"
                      : isDarkMode
                      ? "bg-gray-700"
                      : "bg-gray-300"
                  }`}
                ></div>

                {/* Step 2 */}
                <div className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold transition-all ${
                      currentStep >= 2
                        ? isDarkMode
                          ? "bg-phoenix-600 border-phoenix-600 text-white"
                          : "bg-phoenix-600 border-phoenix-600 text-white"
                        : isDarkMode
                        ? "border-gray-600 text-gray-600"
                        : "border-gray-300 text-gray-400"
                    }`}
                  >
                    2
                  </div>
                  <span
                    className={`ml-2 text-sm font-medium ${
                      currentStep >= 2
                        ? isDarkMode
                          ? "text-gray-200"
                          : "text-gray-900"
                        : isDarkMode
                        ? "text-gray-600"
                        : "text-gray-400"
                    }`}
                  >
                    Additional Details
                  </span>
                </div>
              </div>
            </div>

            {/* Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Vehicle Selection - Step 1 */}
              <div
                className={`rounded-xl shadow-lg border transition-all ${
                  isDarkMode
                    ? "bg-gray-750 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                } ${currentStep === 1 ? "ring-2 ring-phoenix-500" : ""}`}
              >
                <div
                  className={`px-6 py-4 border-b flex items-center justify-between ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                        selectedVehicle
                          ? "bg-green-500 text-white"
                          : isDarkMode
                          ? "bg-phoenix-600 text-white"
                          : "bg-phoenix-600 text-white"
                      }`}
                    >
                      {selectedVehicle ? (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        "1"
                      )}
                    </div>
                    <h2
                      className={`text-lg font-semibold ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                    >
                      Vehicle Selection
                    </h2>
                  </div>
                  {selectedVehicle && (
                    <span
                      className={`text-xs font-medium px-3 py-1 rounded-full ${
                        isDarkMode
                          ? "bg-green-900/50 text-green-300"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      ✓ Selected
                    </span>
                  )}
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    <div className="relative">
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <span className="flex items-center">
                          <svg
                            className="w-5 h-5 mr-2 text-phoenix-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                            />
                          </svg>
                          Search and Select Vehicle{" "}
                          <span className="text-red-500 ml-1">*</span>
                        </span>
                      </label>

                      {!selectedVehicle ? (
                        <div>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <svg
                                className={`h-5 w-5 ${
                                  isDarkMode ? "text-gray-500" : "text-gray-400"
                                }`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                            </div>
                            <input
                              ref={searchInputRef}
                              type="text"
                              placeholder="Type to search by plate, number, tank, or driver name..."
                              value={vehicleSearch}
                              onChange={(e) => {
                                onVehicleSearchChange(e.target.value);
                                setShowVehicleDropdown(true);
                              }}
                              onFocus={() => setShowVehicleDropdown(true)}
                              disabled={isCreating}
                              className={`block w-full pl-10 pr-3 py-3 border-2 rounded-lg shadow-sm focus:outline-none transition-all ${
                                isDarkMode
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                              } disabled:opacity-50`}
                            />
                            {isSearching && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <div
                                  className={`animate-spin rounded-full h-5 w-5 border-b-2 ${
                                    isDarkMode
                                      ? "border-phoenix-400"
                                      : "border-phoenix-600"
                                  }`}
                                />
                              </div>
                            )}
                          </div>

                          {showVehicleDropdown && searchResults.length > 0 && (
                            <div
                              ref={dropdownRef}
                              className={`absolute z-20 mt-2 w-full shadow-xl max-h-[500px] rounded-lg py-2 text-base overflow-auto border-2 ${
                                isDarkMode
                                  ? "bg-gray-800 border-gray-700"
                                  : "bg-white border-gray-200"
                              }`}
                            >
                              <div
                                className={`px-4 py-2 text-xs font-medium ${
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                {searchResults.length} vehicle
                                {searchResults.length !== 1 ? "s" : ""} found
                              </div>
                              {searchResults.slice(0, 15).map((vehicle) => (
                                <button
                                  key={vehicle.id}
                                  type="button"
                                  onClick={() => handleVehicleSelect(vehicle)}
                                  disabled={isCreating}
                                  className={`w-full text-left px-4 py-3 transition-colors border-l-4 border-transparent hover:border-phoenix-500 ${
                                    isDarkMode
                                      ? "hover:bg-gray-700"
                                      : "hover:bg-gray-50"
                                  } disabled:opacity-50`}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span
                                      className={`font-semibold text-base ${
                                        isDarkMode
                                          ? "text-gray-200"
                                          : "text-gray-900"
                                      }`}
                                    >
                                      {vehicle.plate || `#${vehicle.number}`}
                                    </span>
                                    <span
                                      className={`text-sm px-2 py-0.5 rounded ${
                                        isDarkMode
                                          ? "bg-phoenix-900/50 text-phoenix-300"
                                          : "bg-phoenix-100 text-phoenix-700"
                                      }`}
                                    >
                                      Tank: {vehicle.tank || "N/A"}
                                    </span>
                                  </div>
                                  <div className="flex items-center text-sm">
                                    <svg
                                      className={`w-4 h-4 mr-1.5 ${
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }`}
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                      />
                                    </svg>
                                    <span
                                      className={
                                        isDarkMode
                                          ? "text-gray-400"
                                          : "text-gray-600"
                                      }
                                    >
                                      {vehicle.driver ||
                                        vehicle.name_driver ||
                                        "No driver assigned"}
                                    </span>
                                    <span
                                      className={`ml-auto text-xs ${
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }`}
                                    >
                                      ID: {vehicle.id}
                                    </span>
                                  </div>
                                </button>
                              ))}
                              {searchResults.length > 15 && (
                                <div
                                  className={`px-4 py-2 text-xs text-center ${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  Showing first 15 results. Refine your search
                                  to see more.
                                </div>
                              )}
                            </div>
                          )}

                          {showVehicleDropdown &&
                            vehicleSearch &&
                            searchResults.length === 0 &&
                            !isSearching && (
                              <div
                                className={`mt-2 p-6 rounded-lg border-2 border-dashed text-center ${
                                  isDarkMode
                                    ? "border-gray-700 bg-gray-700/30"
                                    : "border-gray-300 bg-gray-50"
                                }`}
                              >
                                <svg
                                  className={`mx-auto h-12 w-12 mb-3 ${
                                    isDarkMode
                                      ? "text-gray-600"
                                      : "text-gray-400"
                                  }`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <p
                                  className={`text-sm font-medium ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  }`}
                                >
                                  No vehicles found for "{vehicleSearch}"
                                </p>
                                <p
                                  className={`text-xs mt-1 ${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  Try a different search term
                                </p>
                              </div>
                            )}
                        </div>
                      ) : (
                        <div
                          className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                            isDarkMode
                              ? "bg-green-900/20 border-green-700"
                              : "bg-green-50 border-green-300"
                          }`}
                        >
                          <div className="flex items-center flex-1">
                            <div
                              className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${
                                isDarkMode
                                  ? "bg-green-800 text-green-300"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              <svg
                                className="w-6 h-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <div>
                              <div
                                className={`font-semibold text-lg ${
                                  isDarkMode
                                    ? "text-green-200"
                                    : "text-green-900"
                                }`}
                              >
                                {selectedVehicle.plate ||
                                  `#${selectedVehicle.number}`}
                              </div>
                              <div
                                className={`text-sm flex items-center mt-1 ${
                                  isDarkMode
                                    ? "text-green-300"
                                    : "text-green-700"
                                }`}
                              >
                                <span className="font-medium">
                                  Tank: {selectedVehicle.tank || "N/A"}
                                </span>
                                <span className="mx-2">•</span>
                                <span>
                                  Driver:{" "}
                                  {selectedVehicle.driver ||
                                    selectedVehicle.name_driver ||
                                    "N/A"}
                                </span>
                                <span className="mx-2">•</span>
                                <span className="text-xs">
                                  ID: {selectedVehicle.id}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleClearVehicle}
                            disabled={isCreating}
                            className={`ml-4 p-2 rounded-lg transition-colors ${
                              isDarkMode
                                ? "text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                : "text-red-600 hover:text-red-800 hover:bg-red-100"
                            } disabled:opacity-50`}
                            title="Change vehicle"
                          >
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Details - Step 2 */}
              {selectedVehicle && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`rounded-xl shadow-lg border transition-all ${
                    isDarkMode
                      ? "bg-gray-750 border-gray-700"
                      : "bg-gray-50 border-gray-200"
                  } ${currentStep === 2 ? "ring-2 ring-phoenix-500" : ""}`}
                >
                  <div
                    className={`px-6 py-4 border-b flex items-center ${
                      isDarkMode ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                        isDarkMode
                          ? "bg-phoenix-600 text-white"
                          : "bg-phoenix-600 text-white"
                      }`}
                    >
                      2
                    </div>
                    <h2
                      className={`text-lg font-semibold ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                    >
                      Additional Details (Optional)
                    </h2>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Driver Name */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <span className="flex items-center">
                            <svg
                              className={`w-4 h-4 mr-2 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            Driver Name
                          </span>
                        </label>
                        <input
                          type="text"
                          value={driverName}
                          onChange={(e) => onDriverNameChange(e.target.value)}
                          placeholder="Enter driver name"
                          disabled={isCreating}
                          className={`block w-full px-4 py-2.5 border rounded-lg shadow-sm focus:outline-none transition-all ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                          } disabled:opacity-50`}
                        />
                      </div>

                      {/* Mechanic Name */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <span className="flex items-center">
                            <svg
                              className={`w-4 h-4 mr-2 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            Mechanic Name
                          </span>
                        </label>
                        <input
                          type="text"
                          value={mechanicName}
                          onChange={(e) => onMechanicNameChange(e.target.value)}
                          placeholder="Enter mechanic name"
                          disabled={isCreating}
                          className={`block w-full px-4 py-2.5 border rounded-lg shadow-sm focus:outline-none transition-all ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-phoenix-500 focus:border-phoenix-500"
                          } disabled:opacity-50`}
                        />
                      </div>
                    </div>

                    {/* Info Banner */}
                    <div
                      className={`mt-6 p-4 rounded-lg border flex items-start ${
                        isDarkMode
                          ? "bg-blue-900/20 border-blue-800"
                          : "bg-blue-50 border-blue-200"
                      }`}
                    >
                      <svg
                        className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <p
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-blue-300" : "text-blue-800"
                          }`}
                        >
                          After creating the inspection, you'll be redirected to
                          fill out the detailed checklist items.
                        </p>
                        <p
                          className={`text-xs mt-1 ${
                            isDarkMode ? "text-blue-400" : "text-blue-600"
                          }`}
                        >
                          This includes brake checks, lights, tires, and all
                          safety components.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div
              className={`px-6 py-4 border-t backdrop-blur-sm ${
                isDarkMode
                  ? "bg-gray-800/95 border-gray-700"
                  : "bg-white/95 border-gray-200"
              }`}
            >
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={onClose}
                  disabled={isCreating}
                  className={`px-6 py-2.5 border-2 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-all ${
                    isDarkMode
                      ? "border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600 focus:ring-phoenix-400"
                      : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-phoenix-500"
                  }`}
                >
                  <span className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Cancel
                  </span>
                </button>
                <button
                  onClick={onCreate}
                  disabled={!selectedVehicle || isCreating}
                  className={`px-8 py-2.5 border-2 border-transparent rounded-lg text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 active:scale-95 shadow-lg ${
                    isDarkMode
                      ? "bg-phoenix-600 hover:bg-phoenix-700 focus:ring-phoenix-400"
                      : "bg-phoenix-600 hover:bg-phoenix-700 focus:ring-phoenix-500"
                  }`}
                >
                  {isCreating ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating Inspection...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Create Inspection
                    </span>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
